import React, { useState, useEffect } from 'react';
import Slider, { createSliderWithTooltip } from 'rc-slider';
import 'rc-slider/assets/index.css';
import { Button } from 'antd';
import './AudioPlayer.css';
import { floatDurationToString } from '../helpers';
import { PauseOutlined, PlayCircleOutlined } from '@ant-design/icons';

const TooltipSlider = createSliderWithTooltip(Slider);

type AudioPlayerProps = {
  playerRef: React.MutableRefObject<HTMLAudioElement | null>;
  src?: string;
  defaultTime: number;
  onTimeUpdate?: (currentTime: number) => void;
  onDurationChange?: (duration: number) => void;
  disabled?: boolean;
};

const AudioPlayer = (props: AudioPlayerProps) => {
  const playerRef = props.playerRef;
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [totalTime, setTotalTime] = useState<number>(0);
  const [isPlaying, setPlaying] = useState<boolean>(false);

  useEffect(() => {
    if (!playerRef.current) {
      throw new Error('tiro/no-player');
    }
    const player = playerRef.current;
    const defaultChunkTime = 4.5; // seconds

    const play = () => setPlaying(true);
    const pause = () => setPlaying(false);
    const durationChange: EventListener = (event: any) =>
      setTotalTime((prevDuration) =>
        Number.isFinite(event.target.duration) ? event.target.duration : prevDuration + defaultChunkTime,
      );
    player.addEventListener('pause', pause);
    player.addEventListener('play', play);
    player.addEventListener('durationchange', durationChange);

    return () => {
      player.removeEventListener('play', play);
      player.removeEventListener('pause', pause);
      player.removeEventListener('durationchange', durationChange);
    };
  }, [playerRef]);

  const onTimeUpdate = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    const { currentTime } = event.target as HTMLAudioElement;
    setCurrentTime(currentTime);
    if (props.onTimeUpdate) {
      props.onTimeUpdate(currentTime);
    }
  };

  const onDurationChange = (event: React.SyntheticEvent<HTMLAudioElement>) => {
    const { duration } = event.target as HTMLAudioElement;
    if (props.onDurationChange) props.onDurationChange(duration);
  };

  const onPlayOrPause = () => {
    if (!playerRef.current) throw new Error('tiro/no-player');
    const player = playerRef.current;
    if (player.paused) {
      player.play();
    } else {
      player.pause();
    }
  };

  const tipFormatter = (value: number) => {
    const time = (totalTime * value) / 100;
    if (!Number.isFinite(time)) {
      return '0.0s';
    }
    return floatDurationToString((totalTime * value) / 100);
  };
  return (
    <div className='audio-player-container' style={{ padding: '10px' }}>
      <audio ref={playerRef} onTimeUpdate={onTimeUpdate} onDurationChange={onDurationChange} />
      <Button
        disabled={props.disabled}
        icon={isPlaying ? <PauseOutlined /> : <PlayCircleOutlined />}
        onClick={onPlayOrPause}
      />
      <TooltipSlider
        disabled={props.disabled}
        marks={{
          100: {
            style: { textAlign: 'right' },
            label: floatDurationToString(totalTime),
          },
        }}
        tipFormatter={tipFormatter}
        min={0}
        max={100}
        value={totalTime > 0 ? Math.round((100 * currentTime) / totalTime) : 0}
        onChange={(value: number) => {
          if (!playerRef.current) {
            throw new Error('tiro/no-player');
          }
          const time = (value * totalTime) / 100;
          if (Number.isFinite(time)) {
            playerRef.current.currentTime = (value * totalTime) / 100;
          }
        }}
      />
    </div>
  );
};

export default React.memo(AudioPlayer);
